/* Couple set builder (WAI-673). See snippets/couple-set-builder.liquid. */
(function () {
  const MESSAGES = {
    unavailable:
      "We couldn't apply the couple set discount just now. Nothing was added to your cart. Please try again in a minute.",
    sold_out: 'One of these pieces just sold out. Please choose another option.',
    not_eligible: "One of these pieces can't be part of a couple set. Please choose another piece.",
    rate_limited: 'Too many tries in a row. Please wait a minute and try again.',
    on_sale: 'One of these pieces is already on sale, so the set discount doesn\'t apply. Both were added at their current price.',
    price_changed: 'Prices just changed. Please reload the page to see the current couple set price.',
    paused: 'Sale prices already apply right now, so the set discount is paused. Both were added at their current price.',
    cart: "We couldn't add these to your cart. Please try again.",
    currency: 'Prices just changed. Please reload the page to see the current couple set price.',
  };
  // Buy with Shop / More payment options: the same cases, worded for going straight to checkout.
  const BUY_MESSAGES = {
    ...MESSAGES,
    unavailable: "We couldn't apply the couple set discount just now. Please try again in a minute.",
    on_sale: 'One of these pieces is already on sale, so the set discount doesn\'t apply. Taking you to checkout at their current price.',
    paused: 'Sale prices already apply right now, so the set discount is paused. Taking you to checkout at their current price.',
    checkout: "We couldn't open checkout just now. Please try again.",
  };
  const STOREFRONT_API = '/api/2025-07/graphql.json';
  const SLOT_NAMES = ['first', 'second'];
  const ROW_GAP = 24; // smallest gap (px) between a card's two options when they share a row

  // format: the store's money format, or { currency } to format in the shopper's currency.
  function formatMoney(cents, format) {
    if (format && format.currency) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: format.currency }).format(cents / 100);
      } catch (e) {
        return `${(cents / 100).toFixed(2)} ${format.currency}`;
      }
    }
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
      if (kind === 'amount_no_decimals') return withCommas;
      if (kind === 'amount_with_comma_separator') return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`;
      return `${withCommas}.${dec}`;
    });
  }

  // Same rule as the couple-set Worker (worker/src/index.js, setDiscountCents):
  // each piece's discount = round(piece price in cents x percent / 100), halves round up;
  // set discount = sum over its pieces; set price = total - set discount.
  function setDiscountCents(pieceCents, percent) {
    return pieceCents.reduce((sum, c) => sum + Math.round((c * percent) / 100), 0);
  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const esc = (v) => String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const handleize = (v) => String(v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  // Liquid's capitalize, as the theme's swatch tooltip uses it ("Sterling silver").
  const capitalize = (v) => String(v).charAt(0).toUpperCase() + String(v).slice(1).toLowerCase();

  class Slot {
    constructor(el, builder, index) {
      this.el = el;
      this.builder = builder;
      this.index = index;
      this.product = null;
      this.selected = [];
      this.img = el.querySelector('[data-cs-piece-img]');
      this.optionsEl = el.querySelector('[data-cs-options]');
      el.querySelector('[data-cs-change]').addEventListener('click', (e) => builder.sheet.open(this, e.currentTarget));
      el.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cs-value]');
        if (input) this.pickOption(Number(input.dataset.csIndex), input.value);
      });
    }

    get variant() {
      if (!this.product) return null;
      return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i])) || null;
    }

    pickProduct(id) {
      const product = this.builder.products.find((p) => p.id === id);
      if (!product) return;
      this.product = product;
      const first = product.variants.find((v) => v.available) || product.variants[0];
      this.selected = [...first.options];
      this.el.querySelector('[data-cs-name]').textContent = product.title;
      this.renderOptions();
      this.render();
    }

    pickOption(index, value) {
      this.selected[index] = value;
      // If that combination doesn't exist, fall back to the closest variant that has this value.
      if (!this.variant) {
        const match =
          this.product.variants.find((v) => v.options[index] === value && v.available) ||
          this.product.variants.find((v) => v.options[index] === value);
        if (match) this.selected = [...match.options];
      }
      this.render();
    }

    valueAvailable(index, value) {
      return this.product.variants.some(
        (v) => v.available && v.options[index] === value && v.options.every((o, i) => i === index || o === this.selected[i])
      );
    }

    // One block per option, label above ("Material:"), in Broadcast's variant-picker markup: colour swatches
    // (tooltip on hover) with the chosen name under them for swatch options such as Material and Color, the
    // theme's option buttons for the rest (the chosen button shows the value, so it isn't repeated).
    renderOptions() {
      const p = this.product;
      this.optionsEl.querySelectorAll('[data-cs-option]').forEach((n) => n.remove());
      p.options.forEach((name, index) => {
        const values = [...new Set(p.variants.map((v) => v.options[index]))];
        const picker = (p.pickers && p.pickers[index]) || {};
        const base = `${this.builder.el.id}-${this.index}-${index}`;
        const items = values
          .map((value, i) => {
            const id = `${base}-${i}`;
            const input = `<input type="radio" id="${id}" name="${base}" value="${esc(value)}" data-cs-value="${esc(value)}" data-cs-index="${index}"`;
            const unavailable = '<span class="visually-hidden label-unavailable" hidden>Sold out</span>';
            if (picker.swatch) {
              const color = picker.colors && picker.colors[value];
              return (
                `<tooltip-component data-tooltip="${esc(capitalize(value))}" class="swatch__button swatch__button--${esc(this.builder.swatchStyle)} swatch-${handleize(value)}"${color ? ` style="--swatch: ${esc(color)};"` : ''}>` +
                `${input} class="swatch-input__input">` +
                `<label for="${id}" title="${esc(value)}" class="swatch-input__label"><span class="visually-hidden">${esc(value)}</span>${unavailable}</label>` +
                '</tooltip-component>'
              );
            }
            return `<span class="radio__button">${input}><label for="${id}"><span class="option-title">${esc(value)}</span>${unavailable}</label></span>`;
          })
          .join('');
        const buttons = picker.swatch
          ? `<div class="radio__buttons"><div class="radio__buttons">${items}</div></div>`
          : `<div class="radio__buttons">${items}</div>`;
        const row = document.createElement('div');
        row.className = `cs-opt radio__fieldset${picker.swatch ? ' radio__fieldset--swatches' : ''}`;
        row.dataset.csOption = index;
        row.setAttribute('role', 'radiogroup');
        row.setAttribute('aria-labelledby', `${base}-label`);
        row.innerHTML =
          `<p class="cs-opt__label body-medium" id="${base}-label">${esc(name)}:</p>${buttons}` +
          (picker.swatch ? '<p class="cs-opt__value body-x-small" data-cs-selected></p>' : '');
        this.optionsEl.appendChild(row);
      });
    }

    // Selection and availability are updated in place (re-rendering would leave the theme's tooltip stuck).
    syncOptions() {
      this.optionsEl.querySelectorAll('[data-cs-option]').forEach((row) => {
        const index = Number(row.dataset.csOption);
        const chosen = row.querySelector('[data-cs-selected]');
        if (chosen) chosen.textContent = this.selected[index] || '';
        row.querySelectorAll('[data-cs-value]').forEach((input) => {
          const off = !this.valueAvailable(index, input.value);
          input.checked = this.selected[index] === input.value;
          input.classList.toggle(input.classList.contains('swatch-input__input') ? 'visually-disabled' : 'disabled', off);
          const note = input.parentElement.querySelector('.label-unavailable');
          if (note) note.hidden = !off;
        });
      });
    }

    render() {
      const v = this.variant;
      this.syncOptions();
      const media = (v && this.product.media.find((m) => m.id === v.media)) || this.product.media[0];
      if (media) {
        this.img.src = media.thumb;
        this.img.hidden = false;
      } else {
        this.img.hidden = true;
      }
      // The piece's price in the set (same rounding as the cart line), then its regular price crossed out.
      const priceEl = this.el.querySelector('[data-cs-price]');
      if (!v) priceEl.textContent = 'Unavailable';
      else if (!v.available) priceEl.textContent = 'Sold out';
      else {
        const money = (c) => formatMoney(c, this.builder.moneyFormat);
        const setPrice = v.price - setDiscountCents([v.price], this.builder.percent);
        priceEl.innerHTML =
          `<span class="cs-piece__now"><span class="visually-hidden visually-hidden--inline">Price in the set </span>${money(setPrice)}</span> ` +
          `<s class="cs-piece__was"><span class="visually-hidden visually-hidden--inline">Regular price </span>${money(v.price)}</s>`;
      }
      this.builder.update();
    }
  }

  // Words in an option value that name a metal or colour, e.g. "Gold Vermeil" -> ["gold"].
  const COLOUR_WORDS = ['silver', 'gold', 'rose', 'black', 'blue', 'white', 'red', 'green', 'brown', 'pink', 'grey', 'gray'];

  // A piece's photos for its chosen variant: the ones whose file name or alt text names the chosen metal or
  // colour (file names say e.g. "Crown-Ring-Gold-Male-Front"), the variant's own photo first. No match: all photos.
  function photosFor(product, variant) {
    if (!product) return [];
    const words = variant ? variant.options.flatMap((o) => String(o).toLowerCase().split(/[^a-z]+/)).filter((w) => COLOUR_WORDS.includes(w)) : [];
    const text = (m) => `${m.name} ${m.alt}`.toLowerCase();
    let photos = product.media.filter((m) => words.every((w) => text(m).includes(w)) && (words.includes('rose') || !text(m).includes('rose')));
    if (!words.length || !photos.length) photos = product.media.slice();
    const own = variant && product.media.find((m) => m.id === variant.media);
    if (own) photos = [own, ...photos.filter((m) => m !== own)];
    return photos;
  }

  // Takes the place of the theme's product gallery on the builder's product page: the two chosen pieces'
  // photos, first piece then second, with the scanning video third (last when there are fewer than two photos).
  // Swipes on phones; thumbnails below. (The theme's own gallery reads its slides once at page load, so it
  // can't be refilled.)
  class Gallery {
    constructor(themeGallery, video) {
      this.el = document.createElement('div');
      this.el.className = 'cs-gallery';
      this.el.innerHTML = '<div class="cs-gallery__track" data-cs-track></div><div class="cs-gallery__thumbs" data-cs-thumbs></div>';
      this.track = this.el.querySelector('[data-cs-track]');
      this.thumbs = this.el.querySelector('[data-cs-thumbs]');
      this.key = '';
      themeGallery.prepend(this.el);
      themeGallery.classList.add('cs-gallery-on');
      this.track.addEventListener('scroll', () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.mark(Math.round(this.track.scrollLeft / this.track.clientWidth)), 60);
      });
      this.video = video ? this.videoSlide(video) : null;
    }

    // The video's slide, made once and moved from pair to pair, so it downloads once and keeps its place in
    // the loop. It starts downloading when the page has finished loading (the first photos come first) and
    // plays only while it's on screen, muted and looping. With reduced motion it waits for the play button.
    videoSlide(v) {
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const slide = document.createElement('div');
      slide.className = 'cs-gallery__slide';
      slide.innerHTML = `<video muted loop playsinline preload="none"${still ? ' controls' : ''}></video>`;
      const video = slide.querySelector('video');
      video.muted = true;
      video.poster = v.poster;
      video.src = v.src;
      video.setAttribute('aria-label', v.alt || '');
      const fetchVideo = () => (video.preload = 'auto');
      if (document.readyState === 'complete') fetchVideo();
      else window.addEventListener('load', fetchVideo, { once: true });
      if (!still && 'IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => (e.isIntersecting ? video.play().catch(() => {}) : video.pause()), { threshold: 0.6 }).observe(slide);
      }
      return { ...v, slide };
    }

    show(photos) {
      const items = photos.slice();
      if (this.video) items.splice(Math.min(2, items.length), 0, this.video);
      const key = items.map((m) => m.id).join(',');
      if (key === this.key) return;
      this.key = key;
      this.track.innerHTML = '';
      this.thumbs.innerHTML = '';
      items.forEach((m, i) => {
        let slide = m.slide;
        if (!slide) {
          slide = document.createElement('div');
          slide.className = 'cs-gallery__slide';
          slide.innerHTML = `<img src="${m.src}" alt="" width="1200" height="1200" loading="${i < 2 ? 'eager' : 'lazy'}">`;
          slide.querySelector('img').alt = m.alt || '';
        }
        this.track.appendChild(slide);
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = m.slide ? 'cs-gallery__thumb cs-gallery__thumb--video' : 'cs-gallery__thumb';
        thumb.setAttribute('aria-label', `${m.slide ? 'Video' : 'Photo'} ${i + 1} of ${items.length}`);
        thumb.innerHTML = `<img src="${m.thumb}" alt="" width="80" height="80" loading="lazy">`;
        thumb.addEventListener('click', () => this.go(i));
        this.thumbs.appendChild(thumb);
      });
      this.track.scrollLeft = 0;
      this.mark(0);
    }

    go(i) {
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' });
      this.mark(i);
    }

    mark(i) {
      [...this.thumbs.children].forEach((t, n) => t.classList.toggle('is-active', n === i));
    }
  }

  // One sheet of all pieces, opened by either slot's "Change". Moved to <body> so the product
  // section's sticky (stacking) container can't clip it.
  class Sheet {
    constructor(el, builder) {
      this.el = el;
      this.builder = builder;
      this.slot = null;
      this.opener = null;
      this.title = el.querySelector('[data-cs-sheet-title]');
      document.body.appendChild(el);
      el.querySelectorAll('[data-cs-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      el.querySelectorAll('[data-cs-product]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (this.slot) this.slot.pickProduct(Number(btn.dataset.csProduct));
          this.close();
        });
      });
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open(slot, opener) {
      this.slot = slot;
      this.opener = opener;
      this.title.textContent = `Choose the ${SLOT_NAMES[slot.index]} piece`;
      this.el.querySelectorAll('[data-cs-product]').forEach((btn) => {
        const on = slot.product && Number(btn.dataset.csProduct) === slot.product.id;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', String(on));
      });
      // Shown first, then .is-open after a style flush (reading offsetHeight) so the slide-up and fade run
      // (couple-set-builder.css).
      clearTimeout(this.hideTimer);
      this.el.hidden = false;
      void this.el.offsetHeight;
      this.el.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      (this.el.querySelector('.cs-choice.is-selected') || this.el.querySelector('[data-cs-close]')).focus({ preventScroll: true });
    }

    close() {
      if (this.el.hidden || !this.slot) return;
      this.el.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (this.opener) this.opener.focus({ preventScroll: true });
      this.slot = null;
      // Hidden once the slide-down has run (0.4s, as in the CSS); at once when the shopper has reduced motion.
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.hideTimer = setTimeout(() => (this.el.hidden = true), still ? 0 : 400);
    }
  }

  class Builder {
    constructor(el) {
      this.el = el;
      this.workerUrl = el.dataset.workerUrl;
      this.percent = Number(el.dataset.percent);
      // Liquid prices are in the shopper's currency; the service checks them in that currency too.
      this.currency = el.dataset.currency || el.dataset.shopCurrency;
      this.country = el.dataset.country;
      this.moneyFormat = this.currency === el.dataset.shopCurrency ? el.dataset.moneyFormat : { currency: this.currency };
      this.swatchStyle = el.dataset.swatchStyle || 'circle';
      this.products = JSON.parse(el.querySelector('[data-cs-products]').textContent);
      const metalsEl = el.querySelector('[data-cs-metals]');
      this.metals = metalsEl ? JSON.parse(metalsEl.textContent) : {};
      this.details = document.querySelector(`[data-cs-details="${el.id}"]`);
      this.addBtn = el.querySelector('[data-cs-add]');
      this.message = el.querySelector('[data-cs-message]');
      this.total = el.querySelector('[data-cs-total]');
      this.terms = el.querySelector('[data-cs-terms]');
      this.buyBtns = [...el.querySelectorAll('[data-cs-buy]')];
      this.busy = false;
      this.sheet = new Sheet(el.querySelector('[data-cs-sheet]'), this);
      const page = el.closest('.product__page');
      const themeGallery = page && page.querySelector('product-images');
      const videoEl = el.querySelector('[data-cs-video]');
      this.gallery = themeGallery ? new Gallery(themeGallery, videoEl ? JSON.parse(videoEl.textContent) : null) : null;
      this.slots = [...el.querySelectorAll('[data-cs-slot]')].map((s, i) => new Slot(s, this, i));
      if (this.addBtn) this.addBtn.addEventListener('click', () => this.add());
      this.buyBtns.forEach((btn) => btn.addEventListener('click', () => this.buy(btn)));
      // Back from Shop Pay or checkout: the page can come back from the browser's cache mid-spinner.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.endBuy();
      });

      // Default pair: the first two pieces that can be bought (the same piece twice if only one can).
      const open = this.products.filter((p) => p.variants.some((v) => v.available));
      const pair = [open[0], open[1] || open[0]].filter(Boolean);
      pair.forEach((p, i) => this.slots[i].pickProduct(p.id));
      this.update();
      window.addEventListener('resize', () => this.alignRows());
      if (document.fonts) document.fonts.ready.then(() => this.alignRows());
    }

    // Each card shows its two options side by side (material, then size). The second option lines up in both
    // cards at the furthest-right spot, no further than halfway, where both fit with at least ROW_GAP after the
    // first. With no shared spot each card takes its own; a card whose options can't share a row stacks them.
    alignRows() {
      const fits = this.slots
        .map((slot) => {
          const row = slot.optionsEl;
          row.classList.remove('is-stacked');
          row.style.removeProperty('--cs-start');
          const opts = row.querySelectorAll(':scope > [data-cs-option]');
          const width = row.clientWidth;
          if (opts.length < 2 || !width) return null;
          const earliest = opts[0].offsetWidth + ROW_GAP;
          const latest = width - opts[1].offsetWidth;
          if (opts.length > 2 || earliest > latest) {
            row.classList.add('is-stacked');
            return null;
          }
          return { row, earliest, latest, ideal: Math.round(width / 2) };
        })
        .filter(Boolean);
      if (!fits.length) return;
      const shared = Math.min(...fits.map((f) => Math.min(f.ideal, f.latest)));
      const together = fits.every((f) => shared >= f.earliest);
      fits.forEach((f) => {
        const start = together ? shared : Math.max(f.earliest, Math.min(f.ideal, f.latest));
        f.row.style.setProperty('--cs-start', `${start}px`);
      });
    }

    update(keepMessage) {
      const variants = this.slots.map((s) => s.variant);
      this.alignRows();
      if (this.gallery) this.gallery.show(this.slots.flatMap((s) => photosFor(s.product, s.variant)));
      const ready = variants.length === 2 && variants.every((v) => v && v.available);
      if (ready) {
        const full = variants[0].price + variants[1].price;
        const off = setDiscountCents([variants[0].price, variants[1].price], this.percent);
        const now = formatMoney(full - off, this.moneyFormat);
        const was = formatMoney(full, this.moneyFormat);
        // The theme's sale price with its "% off" badge (snippets/price.liquid), as on the couple-set product pages.
        this.total.innerHTML =
          '<div class="price product__price price--large body-medium price--on-sale product__price--sale price--show-badge">' +
          '<div class="price__container"><div class="price__sale">' +
          `<span class="price-item price-item--sale price-item--last cs-price__now"><span class="visually-hidden visually-hidden--inline">Couple set price </span>${now}</span>` +
          `<span><s class="price-item price-item--regular cs-price__was"><span class="visually-hidden visually-hidden--inline">Regular price </span>${was}</s></span>` +
          '</div></div>' +
          `<div class="product__sale"><span class="product__price--off"><span>${this.percent}%</span> off</span></div>` +
          '</div>';
        this.showTerms(full - off);
      } else {
        this.total.textContent = '';
        this.showTerms(null);
      }
      if (this.addBtn) this.addBtn.disabled = !ready || this.busy;
      this.buyBtns.forEach((btn) => (btn.disabled = !ready || this.busy));
      if (!keepMessage) this.hideMessage();
      this.showDetails();
    }

    // Product details (snippets/couple-set-details.liquid): each chosen piece, then what both share. A piece's
    // bullets are its picked options (material first) and then the lines of its Product details field, product
    // then variant. Materials always has the sterling silver line (every piece is 925 silver, vermeil included),
    // then a line for each other metal picked; a Material value with no Metal entry shows its name only.
    showDetails() {
      const box = this.details;
      if (!box) return;
      const picked = this.slots.filter((s) => s.product && s.variant);
      const same = picked.length === 2 && picked[0].variant.id === picked[1].variant.id;
      const metals = this.metals['sterling-silver'] ? [this.metals['sterling-silver']] : [];
      const pieces = (same ? picked.slice(0, 1) : picked).map(({ product, variant }) => {
        let metalName = null;
        product.options.forEach((name, i) => {
          if (handleize(name) !== 'material') return;
          const metal = this.metals[handleize(variant.options[i])];
          metalName = metal ? metal.name : variant.options[i];
          if (metal && !metals.includes(metal)) metals.push(metal);
        });
        const lines = metalName ? [metalName] : [];
        product.options.forEach((name, i) => {
          const key = handleize(name);
          const value = variant.options[i];
          if (key === 'material') return;
          if (key === 'ring-size') lines.push(`Ring size ${value}`);
          else if (key === 'chain-length') lines.push(`${value} adjustable chain${metalName ? ` in ${metalName}` : ''}`);
          else lines.push(`${name}: ${value}`);
        });
        lines.push(...(product.details || []), ...(variant.details || []));
        const title = same ? `2 × ${product.title}` : product.title;
        return `<p><strong>${esc(title)}</strong></p><ul>${lines.map((l) => `<li>${esc(l)}</li>`).join('')}</ul>`;
      });
      box.innerHTML =
        pieces.join('') +
        '<p>Each piece holds 50 memories and comes in its own gift box with an instructions &amp; care guide.</p>' +
        (metals.length ? `<p><strong>Materials</strong></p>${metals.map((m) => `<p>${esc(m.text)}</p>`).join('')}` : '') +
        '<p>Designed by Mementum in the USA. Handmade in Bali with &lt;3</p>';
    }

    say(text) {
      this.message.textContent = text;
      this.message.hidden = false;
    }

    hideMessage() {
      this.message.hidden = true;
    }

    // Points Shopify's installments line at the set price. Its data (shopify-meta) lists each variant's price and
    // the store's financing plans; we keep the plans and give it one variant priced at the set price, and it
    // re-renders on the change. If that data ever changes shape, the line is hidden rather than show a wrong amount.
    showTerms(cents) {
      const box = this.terms;
      const el = box && box.querySelector('shopify-payment-terms');
      if (!el) return;
      if (this.termsMeta === undefined) {
        try {
          this.termsMeta = JSON.parse(el.getAttribute('shopify-meta'));
        } catch (e) {
          this.termsMeta = null;
        }
      }
      const meta = this.termsMeta;
      const base = meta && Array.isArray(meta.variants) && meta.variants[0];
      const known = base && ['id', 'full_price_amount', 'price_per_term_amount', 'number_of_payment_terms'].every((k) => k in base);
      if (cents == null || !known) {
        box.hidden = true;
        return;
      }
      const amount = cents / 100;
      // 4 interest-free payments in the usual plan; fewer for small orders (the plan's split-pay count).
      const plan = (meta.financing_plans || []).find((p) => amount >= p.min_price_amount && amount <= p.max_price_amount);
      const splits = plan ? plan.terms.filter((t) => t.loan_type === 'split_pay').map((t) => t.installments_count) : [];
      const count = splits.length ? Math.max(...splits) : base.number_of_payment_terms;
      const perTerm = Math.round(cents / count);
      const variant = {
        ...base,
        full_price: formatMoney(cents, this.moneyFormat),
        full_price_amount: amount,
        price_per_term: formatMoney(perTerm, this.moneyFormat),
        price_per_term_amount: perTerm / 100,
        number_of_payment_terms: count,
        available: true,
      };
      el.setAttribute('shopify-meta', JSON.stringify({ ...meta, variants: [variant] }));
      el.setAttribute('variant-id', String(base.id));
      box.hidden = false;
    }

    // Buy with Shop / More payment options: like those buttons on the other product pages, skip the cart and pay
    // for just this set. A cart of its own holds the two pieces and the set's code (the shopper's cart is left as
    // it is), then the shopper goes to its checkout, in Shop Pay for Buy with Shop.
    async buy(btn) {
      if (this.busy) return;
      const variants = this.slots.map((s) => s.variant);
      if (!variants.every((v) => v && v.available)) return;
      this.busy = true;
      this.buying = btn;
      btn.classList.add('is-loading');
      this.update();
      let leaving = false;

      try {
        const ids = variants.map((v) => v.id);
        const expectedCents = setDiscountCents([variants[0].price, variants[1].price], this.percent);
        const result = await this.requestCode(ids, expectedCents);
        const withoutCode = result.error === 'on_sale' || result.error === 'paused';
        if (result.error && !withoutCode) {
          this.say(BUY_MESSAGES[result.error] || BUY_MESSAGES.unavailable);
          return;
        }
        const set = result.newSet;
        const checkoutUrl = await this.checkoutFor(ids, set, result.code);
        if (!checkoutUrl) {
          this.say(BUY_MESSAGES[result.code ? 'unavailable' : 'checkout']);
          return;
        }
        this.track(variants, set && set.id, Boolean(result.code), btn.dataset.csBuy);
        if (withoutCode) {
          this.say(BUY_MESSAGES[result.error]);
          await sleep(2500);
        }
        const url = new URL(checkoutUrl, window.location.origin);
        if (btn.dataset.csBuy === 'shop_pay') url.searchParams.set('payment', 'shop_pay');
        leaving = true;
        window.location.href = url.toString();
      } catch (e) {
        console.error('couple set:', e);
        this.say(BUY_MESSAGES.checkout);
      } finally {
        if (!leaving) this.endBuy();
      }
    }

    endBuy() {
      if (this.buying) this.buying.classList.remove('is-loading');
      this.buying = null;
      this.busy = false;
      this.update(true);
    }

    // A new cart through the Storefront API (no token needed for carts): the set's two lines, labelled with the
    // set like the builder's cart lines, and its code. Returns its checkout URL, or null if the code doesn't apply.
    async checkoutFor(ids, set, code) {
      const attributes = set ? [{ key: '_couple_set', value: `${set.id}|${ids[0]}|${ids[1]}` }] : [];
      const gid = (id) => `gid://shopify/ProductVariant/${id}`;
      const lines =
        ids[0] === ids[1]
          ? [{ merchandiseId: gid(ids[0]), quantity: 2, attributes }]
          : ids.map((id) => ({ merchandiseId: gid(id), quantity: 1, attributes }));
      const input = { lines, discountCodes: code ? [code] : [] };
      if (this.country) input.buyerIdentity = { countryCode: this.country };
      const fields = 'id checkoutUrl discountCodes { code applicable }';
      const call = async (query, variables) => {
        const res = await fetch(STOREFRONT_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query, variables }),
        });
        if (!res.ok) throw new Error(`storefront ${res.status}`);
        return res.json();
      };
      const created = await call(`mutation($input: CartInput!) { cartCreate(input: $input) { cart { ${fields} } userErrors { message } } }`, { input });
      let cart = created.data && created.data.cartCreate && created.data.cartCreate.cart;
      if (!cart) return null;
      // A just-made code can take a moment to apply; look again before giving up.
      const applies = (c) => !code || c.discountCodes.some((d) => d.applicable && d.code.toUpperCase() === code.toUpperCase());
      for (let attempt = 0; !applies(cart) && attempt < 3; attempt++) {
        await sleep(1000);
        const read = await call(`query($id: ID!) { cart(id: $id) { ${fields} } }`, { id: cart.id });
        cart = (read.data && read.data.cart) || cart;
      }
      return applies(cart) ? cart.checkoutUrl : null;
    }

    async requestCode(variantIds, expectedCents, cart) {
      // Two tries, then give up: the add is blocked when the code can't be made.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const res = await fetch(this.workerUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ variants: variantIds.map(String), expectedCents, cart, currency: this.currency, country: this.country }),
          });
          const body = await res.json().catch(() => ({}));
          if (res.ok && body.code && body.newSet) return body;
          if (res.status < 500 && body.error) return { error: body.error };
        } catch (e) {
          /* network error: retry */
        }
        if (attempt === 0) await sleep(1200);
      }
      return { error: 'unavailable' };
    }

    // The couple-set code on the cart now and the lines labelled with a set (see couple-set-guard.js).
    async currentSets() {
      const g = window.CoupleSetGuard;
      if (!g) return null;
      const cart = await g.internal('cart.js');
      const code = g.couponOn(cart);
      return code ? { code, lines: g.linesFor(cart.items) } : null;
    }

    async cartRequest(path, body) {
      const res = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`${path} ${res.status}`);
      return res.json();
    }

    // Analytics: one custom event per set added, for a custom pixel to forward to GA4 / Meta.
    // Standard product_added_to_cart events still fire for the two pieces.
    // via: 'cart' (Add to cart), 'shop_pay' (Buy with Shop) or 'checkout' (More payment options).
    track(variants, setId, codeApplied, via = 'cart') {
      try {
        if (!window.Shopify || !Shopify.analytics || !Shopify.analytics.publish) return;
        const full = variants[0].price + variants[1].price;
        const off = codeApplied ? setDiscountCents([variants[0].price, variants[1].price], this.percent) : 0;
        Shopify.analytics.publish('mementum:couple_set_added', {
          set_id: setId || null,
          pieces: this.slots.map((s) => ({
            product_id: s.product.id,
            product_title: s.product.title,
            variant_id: s.variant.id,
            variant_title: s.variant.options.join(' / '),
            price: s.variant.price / 100,
          })),
          full_price: full / 100,
          set_price: (full - off) / 100,
          discount: off / 100,
          currency: this.currency,
          code_applied: codeApplied,
          via,
        });
      } catch (e) {
        /* analytics never blocks the add */
      }
    }

    async add() {
      if (this.busy) return;
      const variants = this.slots.map((s) => s.variant);
      if (!variants.every((v) => v && v.available)) return;
      this.busy = true;
      this.addBtn.disabled = true;
      this.addBtn.classList.add('is-loading');
      this.hideMessage();

      try {
        const ids = variants.map((v) => v.id);
        const expectedCents = setDiscountCents([variants[0].price, variants[1].price], this.percent);
        const existing = await this.currentSets().catch(() => null);
        const result = await this.requestCode(ids, expectedCents, existing || undefined);
        const withoutCode = result.error === 'on_sale' || result.error === 'paused';
        if (result.error && !withoutCode) {
          this.say(MESSAGES[result.error] || MESSAGES.unavailable);
          return;
        }

        // Each line carries its set in a hidden property (underscore: not shown in cart or checkout; shown on the admin order).
        const set = result.newSet;
        const properties = set ? { _couple_set: `${set.id}|${ids[0]}|${ids[1]}` } : {};
        const items =
          ids[0] === ids[1]
            ? [{ id: ids[0], quantity: 2, properties }]
            : ids.map((id) => ({ id, quantity: 1, properties }));
        if (result.code && existing) {
          // Sets already in the cart: one new code covers them and this one (Shopify can't combine two
          // couple-set codes). Add the new pieces while the current code still applies, then switch codes.
          // If a step fails, the earlier sets keep their discount.
          const g = window.CoupleSetGuard;
          if (g) g.setPending(result.code, set.id);
          await this.cartRequest('cart/add.js', { items });
          await this.cartRequest('cart/update.js', { discount: result.code });
          if (g) g.clearPending();
        } else {
          // First set: code first, then the items, so the cart never holds this set at full price.
          if (result.code) await this.cartRequest('cart/update.js', { discount: result.code });
          await this.cartRequest('cart/add.js', { items });
        }
        this.track(variants, set && set.id, Boolean(result.code));

        this.addBtn.classList.add('is-added');
        setTimeout(() => this.addBtn.classList.remove('is-added'), 1000);
        if (withoutCode) {
          this.say(MESSAGES[result.error]);
          await sleep(2500);
        }
        this.showCart();
      } catch (e) {
        console.error('couple set:', e);
        this.say(MESSAGES.cart);
      } finally {
        this.busy = false;
        this.addBtn.classList.remove('is-loading');
        this.update(true);
      }
    }
  }

  // After an add: refresh and open the theme's cart drawer, as Broadcast does after its own add to cart
  // (theme:cart:refresh makes <cart-items> re-render from /cart?section_id=api-cart-items, then it fires
  // theme:product:added, which opens <cart-drawer>). Falls back to the cart page without a drawer.
  Builder.prototype.showCart = function () {
    const cartUrl = `${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart`;
    const drawer = document.querySelector('cart-drawer');
    const drawerCart =
      drawer &&
      drawer.querySelector('cart-items') &&
      window.theme &&
      theme.settings &&
      theme.settings.cartType === 'drawer' &&
      customElements.get('cart-drawer');
    if (!drawerCart) {
      window.location.href = cartUrl;
      return;
    }
    let opened = drawer.classList.contains('is-open');
    document.addEventListener('theme:cart-drawer:open', () => (opened = true), { once: true });
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    setTimeout(() => {
      if (!opened) window.location.href = cartUrl;
    }, 8000);
  };

  function init() {
    document.querySelectorAll('[data-cs-builder]').forEach((el) => {
      if (!el.csBuilder) el.csBuilder = new Builder(el);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
